import { useState, type DragEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  FileText,
  Image as ImageIcon,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EnumSelect } from '@/components/ui/enum-select'
import { Input } from '@/components/ui/input'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldHelp,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Separator } from '@/components/ui/separator'
import { CustomerCombobox } from '@/src/components/CustomerCombobox'
import { HintedLabel } from '@/src/components/FieldHint'
import { FIELD_HELP } from '@/src/lib/onboarding/field-help'
import { ServiceTypeMultiSelect } from '@/src/components/ServiceTypeMultiSelect'
import { FormWizard, ReviewRow, ReviewSection } from '@/src/components/wizard/FormWizard'
import { TextAreaField, TextField, formatLabel } from '@/src/components/wizard/fields'
import { useDraftState, useFormDraft } from '@/src/components/wizard/useFormDraft'
import {
  useFormWizard,
  type FieldErrors,
  type WizardStep,
} from '@/src/components/wizard/useFormWizard'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { LocationPicker, locationModeHint } from '@/src/components/LocationPicker'
import { CHINA, KAMPALA, type LocationChoice, type ShipmentMode } from '@/lib/locations'
import { useClassify } from '@/hooks/useClassify'
import { useTaxEstimate } from '@/hooks/useTaxEstimate'
import { useParseInvoice } from '@/hooks/useParseInvoice'
import { uploadInvoiceAsset, uploadProductImage } from '@/lib/storage/images'
import type {
  CreateConsignmentRequest,
  HelmAirService,
  HelmCommunicationChannel,
  HelmInquiryStage,
  HelmServiceType,
  TaxEstimateResponse,
} from '@/lib/api/types'
import { apiClient } from '@/lib/api/client'
import { AIR_SERVICES, shipmentLabel } from '@/src/lib/air-service'

const inquiryStageOptions: HelmInquiryStage[] = [
  'NEW_INQUIRY',
  'QUALIFYING',
  'QUOTED',
  'AWAITING_CUSTOMER',
  'ACTIVE',
  'BLOCKED',
  'COMPLETED',
  'CLOSED',
]

const communicationChannelOptions: HelmCommunicationChannel[] = [
  'EMAIL',
  'WHATSAPP',
  'BOTH',
  'NONE',
]

const modeOptions = [
  { value: 'air', label: 'Air' },
  { value: 'sea', label: 'Sea' },
  { value: 'truck', label: 'Truck' },
]

const options = (values: readonly string[]) =>
  values.map((value) => ({ value, label: formatLabel(value) }))

const INVOICE_TYPES = /^(application\/pdf|image\/(jpeg|png|jpg|gif|webp))$/

type StepId = 'customer' | 'shipping' | 'invoice' | 'items' | 'review'

const steps: WizardStep<StepId>[] = [
  {
    id: 'customer',
    title: 'Customer & service',
    description: 'Who the consignment is for, and what Xtellar is doing for them.',
  },
  {
    id: 'shipping',
    title: 'Shipping',
    description: 'How and where the goods travel.',
  },
  {
    id: 'invoice',
    title: 'Invoice',
    description: 'Upload the supplier invoice and Helm lists its items for you.',
    optional: true,
  },
  {
    id: 'items',
    title: 'Items & HS codes',
    description: 'What is in the consignment, and how each item is classified.',
  },
  {
    id: 'review',
    title: 'Review & tax',
    description: 'Check the consignment and estimate the import taxes before creating it.',
  },
]

interface ItemDraft {
  id: string
  name: string
  quantity: number
  unitPrice: number
  description: string
  hsCode?: string
  hsCodeDescription?: string
  aiConfidence?: number
  imageUrl?: string
}

const newItem = (patch: Partial<ItemDraft> = {}): ItemDraft => ({
  id: crypto.randomUUID(),
  name: '',
  quantity: 1,
  unitPrice: 0,
  description: '',
  ...patch,
})

const money = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function NewConsignmentPage() {
  const navigate = useNavigate()
  const draft = useFormDraft('new-consignment')
  const { data: customers, loading: customersLoading } = useAsyncData(
    () => apiClient.listCustomers(),
    [],
  )
  const [customerId, setCustomerId] = useDraftState(draft, 'customerId', '')
  const [serviceTypes, setServiceTypes] = useDraftState<HelmServiceType[]>(draft, 'serviceTypes', [
    'END_TO_END_IMPORT',
  ])
  const [inquiryStage, setInquiryStage] = useDraftState<HelmInquiryStage>(
    draft,
    'inquiryStage',
    'NEW_INQUIRY',
  )
  const [channel, setChannel] = useDraftState<HelmCommunicationChannel>(draft, 'channel', 'EMAIL')
  const [nextAction, setNextAction] = useDraftState(draft, 'nextAction', '')
  const [serviceNotes, setServiceNotes] = useDraftState(draft, 'serviceNotes', '')
  const [mode, setMode] = useDraftState(draft, 'mode', '')
  // For air only: freight, express, courier or a chartered aircraft.
  const [airService, setAirService] = useDraftState<HelmAirService | ''>(draft, 'airService', '')
  const [origin, setOrigin] = useDraftState<LocationChoice | null>(draft, 'origin', CHINA)
  const [destination, setDestination] = useDraftState<LocationChoice | null>(
    draft,
    'destination',
    KAMPALA,
  )
  const shipment: ShipmentMode =
    mode === 'air' ? 'AIR' : mode === 'sea' ? 'SEA' : mode === 'truck' ? 'ROAD' : 'NONE'
  const [expectedArrival, setExpectedArrival] = useDraftState(draft, 'expectedArrival', '')
  const [trackingNumber, setTrackingNumber] = useDraftState(draft, 'trackingNumber', '')
  const [insurance, setInsurance] = useDraftState<'yes' | 'no'>(draft, 'insurance', 'no')
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null)
  const [invoiceNotice, setInvoiceNotice] = useState<{ ok: boolean; text: string } | null>(null)
  const [parsingInvoice, setParsingInvoice] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [items, setItems] = useDraftState<ItemDraft[]>(draft, 'items', [])
  const [itemNotices, setItemNotices] = useState<Record<string, string>>({})
  const [uploadingImage, setUploadingImage] = useState<string | null>(null)
  const [classifying, setClassifying] = useState<string[]>([])
  const [taxEstimate, setTaxEstimate] = useDraftState<TaxEstimateResponse | null>(
    draft,
    'taxEstimate',
    null,
  )
  const [taxNotice, setTaxNotice] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { classify } = useClassify()
  const { estimate, loading: taxLoading, error: taxError } = useTaxEstimate()
  const { parse } = useParseInvoice()

  const totalValue = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0)
  const selectedCustomer = customers?.find((customer) => customer.id === customerId) ?? null

  function validate(id: StepId): FieldErrors {
    const errors: FieldErrors = {}
    if (id === 'customer') {
      if (!customerId) errors.customer = 'Choose the customer this consignment is for.'
      if (!serviceTypes.length) errors.serviceTypes = 'Choose at least one service.'
    }
    if (id === 'shipping') {
      if (!mode) errors.mode = 'Choose how the goods travel.'
      if (!destination?.label) errors.destination = 'Choose where the goods are going.'
    }
    if (id === 'items') {
      if (!items.length) errors['add-item'] = 'Add at least one item.'
      items.forEach((item) => {
        if (!item.name.trim()) errors[`item-${item.id}-name`] = 'Name the item.'
        if (!(item.quantity >= 1)) errors[`item-${item.id}-quantity`] = 'At least 1.'
        if (!(item.unitPrice >= 0)) errors[`item-${item.id}-price`] = 'Enter 0 or more.'
      })
    }
    return errors
  }

  const wizard = useFormWizard(steps, validate)
  const errors = wizard.errors

  function updateItem(id: string, patch: Partial<ItemDraft>) {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  }

  function setItemNotice(id: string, text: string | null) {
    setItemNotices(({ [id]: _previous, ...rest }) => (text ? { ...rest, [id]: text } : rest))
  }

  async function handleInvoiceUpload(file: File | undefined) {
    if (!file) return
    if (!INVOICE_TYPES.test(file.type)) {
      setInvoiceNotice({ ok: false, text: 'Use a PDF or an image of the invoice.' })
      return
    }
    setInvoiceFile(file)
    setInvoiceNotice(null)
    setParsingInvoice(true)
    try {
      const publicUrl = await uploadInvoiceAsset(file)
      const parsed = await parse(publicUrl)
      if (parsed && parsed.items.length > 0) {
        setItems((current) => [
          ...current,
          ...parsed.items.map((item) =>
            newItem({
              name: item.name,
              quantity: item.quantity,
              unitPrice: item.unit_price,
              description: item.description || '',
            }),
          ),
        ])
        setInvoiceNotice({
          ok: true,
          text: `Found ${parsed.items.length} item${parsed.items.length === 1 ? '' : 's'}. Check them in the next step.`,
        })
      } else {
        setInvoiceNotice({
          ok: false,
          text: 'No items could be read from this invoice. Add them by hand in the next step.',
        })
      }
    } catch (error) {
      setInvoiceNotice({
        ok: false,
        text: `${error instanceof Error ? error.message : 'The invoice could not be read'}. Add the items by hand in the next step.`,
      })
    } finally {
      setParsingInvoice(false)
    }
  }

  function handleDrop(event: DragEvent) {
    event.preventDefault()
    setDragging(false)
    void handleInvoiceUpload(event.dataTransfer.files?.[0])
  }

  async function handleImageUpload(itemId: string, file: File) {
    setUploadingImage(itemId)
    setItemNotice(itemId, null)
    try {
      updateItem(itemId, { imageUrl: await uploadProductImage(file) })
    } catch (error) {
      setItemNotice(
        itemId,
        error instanceof Error ? error.message : 'The image could not be uploaded',
      )
    } finally {
      setUploadingImage(null)
    }
  }

  async function classifyItem(item: ItemDraft) {
    const result = await classify({
      description: item.description || item.name,
      image_url: item.imageUrl,
    })
    if (!result) return null
    updateItem(item.id, {
      hsCode: result.hs_code,
      aiConfidence: result.confidence,
      hsCodeDescription: result.description,
    })
    return result.hs_code
  }

  async function handleClassifyItem(item: ItemDraft) {
    setItemNotice(item.id, null)
    setClassifying((current) => [...current, item.id])
    try {
      const code = await classifyItem(item)
      if (!code) setItemNotice(item.id, 'Helm could not classify this item. Enter the HS code.')
    } finally {
      setClassifying((current) => current.filter((id) => id !== item.id))
    }
  }

  async function handleCalculateTax() {
    setTaxNotice(null)
    const item = items[0]
    if (!item || totalValue === 0) {
      setTaxNotice('Add items with a price before estimating tax.')
      return
    }

    let hsCode = item.hsCode || (await classifyItem(item))
    if (!hsCode) {
      setTaxNotice('Classify the first item, or enter its HS code, before estimating tax.')
      return
    }

    const request = {
      hs_code: hsCode,
      invoice_value: totalValue,
      freight: 0,
      insurance: insurance === 'yes' ? totalValue * 0.02 : 0,
    }
    let result = await estimate(request)
    if (!result) {
      const fallback = await classifyItem(item)
      if (fallback && fallback !== hsCode) {
        hsCode = fallback
        result = await estimate({ ...request, hs_code: fallback })
      }
    }
    if (result) setTaxEstimate(result)
  }

  async function handleSubmit() {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const requestData: CreateConsignmentRequest = {
        customer_id: customerId,
        mode: mode as 'air' | 'sea' | 'truck',
        air_service: mode === 'air' && airService ? airService : null,
        service_type: serviceTypes[0],
        service_types: serviceTypes,
        inquiry_stage: inquiryStage,
        preferred_communication_channel: channel,
        next_action: nextAction.trim() || undefined,
        service_notes: serviceNotes.trim() || undefined,
        destination_port: destination?.label ?? '',
        destination_location_code: destination?.code ?? null,
        origin_country: origin?.country ?? (origin?.label || undefined),
        origin_location_code: origin?.code ?? null,
        expected_arrival: expectedArrival || undefined,
        tracking_number: trackingNumber || undefined,
        insurance: insurance === 'yes',
        items: items.map((item) => ({
          name: item.name.trim(),
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          hs_code: item.hsCode,
          hs_code_description: item.hsCodeDescription,
          ai_confidence: item.aiConfidence,
          image_url: item.imageUrl,
        })),
        tax_estimate: taxEstimate
          ? {
              hs_code: taxEstimate.hs_code,
              cif_value: taxEstimate.input_cif,
              currency: taxEstimate.input_currency || 'USD',
              target_currency: taxEstimate.target_currency || 'UGX',
              exchange_rate: taxEstimate.exchange_rate,
              import_duty: taxEstimate.import_duty,
              vat: taxEstimate.vat,
              withholding_tax: taxEstimate.withholding_tax,
              infra_levy: taxEstimate.infra_levy,
              excise_duty: taxEstimate.excise_duty,
              total_tax: taxEstimate.total_tax,
              breakdown: taxEstimate.breakdown,
            }
          : undefined,
      }

      const created = await apiClient.createConsignment(requestData)
      draft.clear()
      navigate(`/consignments/${created.id}`)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create the consignment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const disabled = isSubmitting
  const taxRows = taxEstimate
    ? ([
        ['Import duty', taxEstimate.import_duty],
        ['VAT', taxEstimate.vat],
        ['Withholding tax', taxEstimate.withholding_tax],
        ['Excise duty', taxEstimate.excise_duty],
        ['Infrastructure levy', taxEstimate.infra_levy],
      ] as const)
    : []

  return (
    <div className="space-y-6">
      <PageHeader
        title="New Consignment"
        description="Open a consignment for goods already on the move or about to ship."
        breadcrumbs={[
          { label: 'Consignments', href: '/consignments' },
          { label: 'New Consignment' },
        ]}
        actions={
          <Button variant="outline" asChild>
            <Link to="/consignments">
              <X className="h-4 w-4" />
              Cancel
            </Link>
          </Button>
        }
      />

      <FormWizard
        wizard={wizard}
        submitLabel="Create consignment"
        busy={isSubmitting}
        busyLabel="Creating consignment"
        error={submitError}
        errorTitle="The consignment was not created"
        onSubmit={handleSubmit}
        draft={draft}
        draftLabel="consignment answers"
      >
        {wizard.step.id === 'customer' && (
          <FieldGroup data-tour="consignment-customer">
            <Field data-invalid={Boolean(errors.customer)}>
              <FieldLabel htmlFor="customer" required>
                Customer
              </FieldLabel>
              <CustomerCombobox
                id="customer"
                customers={customers ?? []}
                value={customerId}
                onValueChange={setCustomerId}
                disabled={disabled || customersLoading || !customers?.length}
                placeholder={customersLoading ? 'Loading customers...' : 'Search customers'}
                aria-invalid={Boolean(errors.customer)}
                aria-describedby={errors.customer ? 'customer-error' : undefined}
              />
              {!customersLoading && !customers?.length && (
                <FieldDescription>
                  <Link to="/customers/new" className="font-medium underline">
                    Add a customer
                  </Link>{' '}
                  before opening a consignment.
                </FieldDescription>
              )}
              <FieldError id="customer-error">{errors.customer}</FieldError>
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field data-invalid={Boolean(errors.serviceTypes)}>
                <HintedLabel htmlFor="serviceTypes" required hint={FIELD_HELP.services}>
                  Services
                </HintedLabel>
                <ServiceTypeMultiSelect
                  id="serviceTypes"
                  value={serviceTypes}
                  onValueChange={setServiceTypes}
                  disabled={disabled}
                />
                <FieldError>{errors.serviceTypes}</FieldError>
              </Field>
              <Field>
                <HintedLabel htmlFor="inquiryStage" hint={FIELD_HELP.consignmentStage}>
                  Stage
                </HintedLabel>
                <EnumSelect
                  value={inquiryStage}
                  onValueChange={(value) => setInquiryStage(value as HelmInquiryStage)}
                  options={options(inquiryStageOptions)}
                  disabled={disabled}
                  triggerProps={{ id: 'inquiryStage' }}
                />
              </Field>
              <Field>
                <HintedLabel htmlFor="channel" hint={FIELD_HELP.reachBy}>
                  Reach them by
                </HintedLabel>
                <EnumSelect
                  value={channel}
                  onValueChange={(value) => setChannel(value as HelmCommunicationChannel)}
                  options={options(communicationChannelOptions)}
                  disabled={disabled}
                  triggerProps={{ id: 'channel' }}
                />
              </Field>
              <TextField
                id="nextAction"
                label="Next action"
                hint={FIELD_HELP.nextAction}
                value={nextAction}
                onChange={setNextAction}
                disabled={disabled}
                placeholder="Quote, chase invoice, confirm PVoC..."
              />
            </div>
            <TextAreaField
              id="serviceNotes"
              label="Service notes"
              value={serviceNotes}
              onChange={setServiceNotes}
              disabled={disabled}
            />
          </FieldGroup>
        )}

        {wizard.step.id === 'shipping' && (
          <FieldGroup>
            <div className="grid gap-4 sm:grid-cols-2" data-tour="consignment-shipping">
              <Field data-invalid={Boolean(errors.mode)}>
                <HintedLabel htmlFor="mode" required hint={FIELD_HELP.shippingModeConsignment}>
                  Mode
                </HintedLabel>
                <EnumSelect
                  value={mode}
                  onValueChange={setMode}
                  options={modeOptions}
                  placeholder="Choose air, sea or truck"
                  disabled={disabled}
                  triggerProps={{
                    id: 'mode',
                    'aria-invalid': Boolean(errors.mode) || undefined,
                    'aria-describedby': errors.mode ? 'mode-error' : undefined,
                  }}
                />
                <FieldError id="mode-error">{errors.mode}</FieldError>
              </Field>
              {mode === 'air' && (
                <Field>
                  <HintedLabel htmlFor="air-service" hint={FIELD_HELP.airService}>
                    Air service
                  </HintedLabel>
                  <EnumSelect
                    value={airService}
                    onValueChange={(value) => setAirService(value as HelmAirService)}
                    options={AIR_SERVICES}
                    placeholder="Freight, express, courier or charter"
                    disabled={disabled}
                    triggerProps={{ id: 'air-service' }}
                  />
                  {airService && (
                    <FieldDescription>
                      {AIR_SERVICES.find((service) => service.value === airService)?.description}
                    </FieldDescription>
                  )}
                </Field>
              )}
              <Field>
                <HintedLabel htmlFor="origin" hint={FIELD_HELP.origin}>
                  Origin
                </HintedLabel>
                <LocationPicker
                  id="origin"
                  aria-label="Origin"
                  role="origin"
                  mode={shipment}
                  value={origin}
                  onChange={setOrigin}
                  disabled={disabled}
                />
                {locationModeHint(origin, 'origin', shipment) && (
                  <FieldDescription>
                    {locationModeHint(origin, 'origin', shipment)}
                  </FieldDescription>
                )}
              </Field>
              <Field data-invalid={Boolean(errors.destination)}>
                <HintedLabel htmlFor="destination" required hint={FIELD_HELP.destination}>
                  Destination
                </HintedLabel>
                <LocationPicker
                  id="destination"
                  aria-label="Destination"
                  role="destination"
                  mode={shipment}
                  value={destination}
                  onChange={setDestination}
                  disabled={disabled}
                  aria-invalid={Boolean(errors.destination)}
                  aria-describedby={errors.destination ? 'destination-error' : undefined}
                />
                {locationModeHint(destination, 'destination', shipment) && (
                  <FieldDescription>
                    {locationModeHint(destination, 'destination', shipment)}
                  </FieldDescription>
                )}
                <FieldError id="destination-error">{errors.destination}</FieldError>
              </Field>
              <TextField
                id="expectedArrival"
                label="Expected arrival"
                hint={FIELD_HELP.expectedArrival}
                type="date"
                value={expectedArrival}
                onChange={setExpectedArrival}
                disabled={disabled}
              />
              <TextField
                id="trackingNumber"
                label="Tracking number"
                hint={FIELD_HELP.trackingNumber}
                value={trackingNumber}
                onChange={setTrackingNumber}
                disabled={disabled}
                placeholder="B/L, AWB or container number"
              />
              <Field>
                <HintedLabel htmlFor="insurance" hint={FIELD_HELP.insurance}>
                  Insurance
                </HintedLabel>
                <EnumSelect
                  value={insurance}
                  onValueChange={(value) => setInsurance(value as 'yes' | 'no')}
                  options={[
                    { value: 'no', label: 'Not insured' },
                    { value: 'yes', label: 'Insured' },
                  ]}
                  disabled={disabled}
                  triggerProps={{ id: 'insurance' }}
                />
              </Field>
            </div>
          </FieldGroup>
        )}

        {wizard.step.id === 'invoice' && (
          <FieldGroup data-tour="consignment-invoice">
            {invoiceFile ? (
              <div className="flex items-center gap-3 rounded-md border p-3">
                <FileText className="size-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{invoiceFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {parsingInvoice
                      ? 'Reading the items…'
                      : `${(invoiceFile.size / 1024).toFixed(0)} KB`}
                  </p>
                </div>
                {parsingInvoice ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove the invoice"
                    onClick={() => {
                      setInvoiceFile(null)
                      setInvoiceNotice(null)
                    }}
                  >
                    <X />
                  </Button>
                )}
              </div>
            ) : (
              <label
                htmlFor="invoice-upload"
                onDragOver={(event) => {
                  event.preventDefault()
                  setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                className={`flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed p-10 text-center text-sm text-muted-foreground hover:bg-accent ${dragging ? 'border-primary bg-primary/5' : ''}`}
              >
                <Upload className="size-6" />
                <span className="font-medium text-foreground">
                  Drop the invoice here, or choose a file
                </span>
                <span className="text-xs">A PDF or an image</span>
              </label>
            )}
            <input
              id="invoice-upload"
              type="file"
              accept=".pdf,image/*"
              className="sr-only"
              disabled={parsingInvoice || disabled}
              onChange={(event) => {
                void handleInvoiceUpload(event.target.files?.[0])
                event.target.value = ''
              }}
            />
            {invoiceNotice && (
              <Alert variant={invoiceNotice.ok ? 'default' : 'destructive'} role="status">
                {invoiceNotice.ok && <CheckCircle2 />}
                <AlertDescription>{invoiceNotice.text}</AlertDescription>
              </Alert>
            )}
            <p className="text-sm text-muted-foreground">
              No invoice yet? Continue, and add the items by hand.
            </p>
          </FieldGroup>
        )}

        {wizard.step.id === 'items' && (
          <FieldGroup data-tour="consignment-items">
            {items.length === 0 && (
              <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                No items yet.
              </div>
            )}
            {items.map((item, index) => (
              <FieldSet key={item.id} className="rounded-md border p-4">
                <div className="flex items-center justify-between gap-3">
                  <FieldLegend variant="label" className="mb-0">
                    Item {index + 1}
                  </FieldLegend>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setItems((current) => current.filter((i) => i.id !== item.id))}
                    disabled={disabled}
                    aria-label={`Remove item ${index + 1}`}
                  >
                    <Trash2 />
                  </Button>
                </div>
                <div className="grid gap-4 sm:grid-cols-4">
                  <TextField
                    id={`item-${item.id}-name`}
                    label="Name"
                    className="sm:col-span-2"
                    value={item.name}
                    onChange={(value) => updateItem(item.id, { name: value })}
                    error={errors[`item-${item.id}-name`]}
                    disabled={disabled}
                    placeholder="e.g. Laptop"
                  />
                  <TextField
                    id={`item-${item.id}-quantity`}
                    label="Quantity"
                    type="number"
                    min={1}
                    inputMode="numeric"
                    value={item.quantity ? String(item.quantity) : ''}
                    onChange={(value) => updateItem(item.id, { quantity: Number(value) })}
                    error={errors[`item-${item.id}-quantity`]}
                    disabled={disabled}
                  />
                  <TextField
                    id={`item-${item.id}-price`}
                    label="Unit price (USD)"
                    hint={FIELD_HELP.unitPrice}
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={Number.isFinite(item.unitPrice) ? String(item.unitPrice) : ''}
                    onChange={(value) => updateItem(item.id, { unitPrice: Number(value) })}
                    error={errors[`item-${item.id}-price`]}
                    disabled={disabled}
                    description={`Total ${money(item.quantity * item.unitPrice)}`}
                  />
                  <TextAreaField
                    id={`item-${item.id}-description`}
                    label="Description"
                    className="sm:col-span-4"
                    rows={2}
                    value={item.description}
                    onChange={(value) => updateItem(item.id, { description: value })}
                    disabled={disabled}
                    placeholder="Material, use and model help Helm find the right HS code."
                  />
                  <div className="sm:col-span-2">
                    <Field>
                      <HintedLabel
                        htmlFor={`item-${item.id}-hs`}
                        hint={FIELD_HELP.consignmentHsCode}
                      >
                        HS code
                      </HintedLabel>
                      <div className="flex gap-2">
                        <Input
                          id={`item-${item.id}-hs`}
                          value={item.hsCode ?? ''}
                          onChange={(event) =>
                            updateItem(item.id, {
                              hsCode: event.target.value,
                              aiConfidence: undefined,
                            })
                          }
                          disabled={disabled}
                          placeholder="0000.00.00"
                          className="font-mono"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => void handleClassifyItem(item)}
                          disabled={
                            disabled ||
                            classifying.includes(item.id) ||
                            (!item.description.trim() && !item.name.trim() && !item.imageUrl)
                          }
                        >
                          {classifying.includes(item.id) ? (
                            <Loader2 className="animate-spin" />
                          ) : (
                            <Sparkles />
                          )}
                          Classify
                        </Button>
                      </div>
                      {item.hsCodeDescription && (
                        <FieldDescription>
                          {item.aiConfidence != null && (
                            <Badge variant="outline" className="mr-1.5">
                              AI {Math.round(item.aiConfidence * 100)}%
                            </Badge>
                          )}
                          {item.hsCodeDescription}
                        </FieldDescription>
                      )}
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field>
                      <FieldLabel htmlFor={`item-${item.id}-image`}>
                        <ImageIcon className="size-4" />
                        Photo
                      </FieldLabel>
                      {item.imageUrl ? (
                        <div className="flex items-center gap-2">
                          <img
                            src={item.imageUrl}
                            alt={item.name || `Item ${index + 1}`}
                            className="size-16 rounded border object-cover"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Remove the photo"
                            onClick={() => updateItem(item.id, { imageUrl: undefined })}
                            disabled={disabled}
                          >
                            <X />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            id={`item-${item.id}-image`}
                            type="file"
                            accept="image/*"
                            className="text-sm file:mr-3 file:rounded-md file:border file:bg-transparent file:px-3 file:py-1.5 file:text-sm"
                            disabled={disabled || uploadingImage === item.id}
                            onChange={(event) => {
                              const file = event.target.files?.[0]
                              if (file) void handleImageUpload(item.id, file)
                            }}
                          />
                          {uploadingImage === item.id && (
                            <Loader2 className="size-4 animate-spin" />
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                  {itemNotices[item.id] && (
                    <p role="alert" className="text-sm text-destructive sm:col-span-4">
                      {itemNotices[item.id]}
                    </p>
                  )}
                </div>
              </FieldSet>
            ))}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                id="add-item"
                type="button"
                variant="outline"
                onClick={() => setItems((current) => [...current, newItem()])}
                disabled={disabled}
                aria-describedby={errors['add-item'] ? 'add-item-error' : undefined}
              >
                <Plus />
                Add item
              </Button>
              {items.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  Invoice total USD {money(totalValue)}
                </span>
              )}
            </div>
            <FieldError id="add-item-error">{errors['add-item']}</FieldError>
          </FieldGroup>
        )}

        {wizard.step.id === 'review' && (
          <div className="space-y-6">
            <div className="space-y-3">
              <ReviewSection title="Customer & service" onEdit={() => wizard.goToStep('customer')}>
                <ReviewRow label="Customer">
                  {selectedCustomer
                    ? selectedCustomer.company_name || selectedCustomer.name
                    : undefined}
                </ReviewRow>
                <ReviewRow label="Services">{serviceTypes.map(formatLabel).join(', ')}</ReviewRow>
                <ReviewRow label="Stage">{formatLabel(inquiryStage)}</ReviewRow>
                <ReviewRow label="Reach them by">{formatLabel(channel)}</ReviewRow>
                <ReviewRow label="Next action">{nextAction.trim()}</ReviewRow>
              </ReviewSection>
              <ReviewSection title="Shipping" onEdit={() => wizard.goToStep('shipping')}>
                <ReviewRow label="Mode">
                  {mode === 'air' && airService
                    ? shipmentLabel('AIR', airService)
                    : modeOptions.find((option) => option.value === mode)?.label}
                </ReviewRow>
                <ReviewRow label="Origin">{origin?.label}</ReviewRow>
                <ReviewRow label="Destination">{destination?.label}</ReviewRow>
                <ReviewRow label="Expected arrival">{expectedArrival}</ReviewRow>
                <ReviewRow label="Tracking number">{trackingNumber.trim()}</ReviewRow>
                <ReviewRow label="Insurance">
                  {insurance === 'yes' ? 'Insured' : 'Not insured'}
                </ReviewRow>
              </ReviewSection>
              <ReviewSection
                title={`Items (${items.length})`}
                onEdit={() => wizard.goToStep('items')}
              >
                {items.map((item, index) => (
                  <ReviewRow key={item.id} label={item.hsCode || 'Not classified'}>
                    {item.name || `Item ${index + 1}`} · {item.quantity} × {money(item.unitPrice)} ={' '}
                    USD {money(item.quantity * item.unitPrice)}
                  </ReviewRow>
                ))}
                <ReviewRow label="Invoice total">USD {money(totalValue)}</ReviewRow>
              </ReviewSection>
            </div>

            <section
              aria-labelledby="tax-heading"
              className="space-y-3 rounded-md border p-4"
              data-tour="consignment-tax"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 id="tax-heading" className="font-medium">
                      Tax estimate
                    </h3>
                    <FieldHelp label="How is the tax estimate worked out?">
                      {FIELD_HELP.taxEstimate}
                    </FieldHelp>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Optional. Saved with the consignment when you calculate it.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleCalculateTax()}
                  disabled={disabled || taxLoading || items.length === 0}
                >
                  {taxLoading && <Loader2 className="animate-spin" />}
                  {taxEstimate ? 'Recalculate' : 'Calculate tax'}
                </Button>
              </div>
              {(taxNotice || taxError) && (
                <Alert>
                  <AlertDescription>{taxNotice || taxError}</AlertDescription>
                </Alert>
              )}
              {taxEstimate && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Exchange rate</span>
                    <span>
                      1 {taxEstimate.input_currency} = {taxEstimate.exchange_rate.toFixed(2)}{' '}
                      {taxEstimate.target_currency}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>CIF value</span>
                    <span>
                      {taxEstimate.target_currency} {taxEstimate.cif_ugx.toLocaleString()}
                    </span>
                  </div>
                  {taxRows
                    .filter(
                      ([label, value]) => value > 0 || label === 'Import duty' || label === 'VAT',
                    )
                    .map(([label, value]) => (
                      <div key={label} className="flex justify-between">
                        <span>{label}</span>
                        <span>
                          {taxEstimate.target_currency} {value.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  <Separator />
                  <div className="flex justify-between text-base font-semibold">
                    <span>Total tax</span>
                    <span>
                      {taxEstimate.target_currency} {taxEstimate.total_tax.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </FormWizard>
    </div>
  )
}
